import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Building, User, Wrench, Plus, Shield, BarChart3, CreditCard, Edit, Trash2, Package } from "lucide-react";
import { Equipamentos } from "./Equipamentos";
import { Planos } from "./Planos";
import { StatusCustomizados } from "./StatusCustomizados";
import { FormasPagamento } from "./FormasPagamento";

import { useEmpresasResponsaveis, useCreateEmpresaResponsavel, useEditarEmpresaResponsavel, useExcluirEmpresaResponsavel } from "@/hooks/useEmpresasResponsaveis";
import { useConsultores, useCreateConsultor, useEditarConsultor, useExcluirConsultor } from "@/hooks/useConsultores";
import { useInstaladores, useCreateInstalador, useEditarInstalador, useExcluirInstalador } from "@/hooks/useInstaladores";
import { useFornecedores, useCreateFornecedor, useEditarFornecedor, useExcluirFornecedor } from "@/hooks/useFornecedores";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

export function Cadastros() {
  const { profile } = useAuth();
  const isTi = profile?.role === 'ti';
  const isAdmin = profile?.role === 'admin' || isTi;
  const isOperator = profile?.role === 'operator' || isTi;
  
  const { data: empresas = [] } = useEmpresasResponsaveis();
  const { data: consultores = [] } = useConsultores();
  const { data: instaladores = [] } = useInstaladores();
  const { data: fornecedores = [] } = useFornecedores(false);

  const createEmpresa = useCreateEmpresaResponsavel();
  const editarEmpresa = useEditarEmpresaResponsavel();
  const excluirEmpresa = useExcluirEmpresaResponsavel();
  const createConsultor = useCreateConsultor();
  const editarConsultor = useEditarConsultor();
  const excluirConsultor = useExcluirConsultor();
  const createInstalador = useCreateInstalador();
  const editarInstalador = useEditarInstalador();
  const excluirInstalador = useExcluirInstalador();
  const createFornecedor = useCreateFornecedor();
  const editarFornecedor = useEditarFornecedor();
  const excluirFornecedor = useExcluirFornecedor();

  const [dialogEmpresa, setDialogEmpresa] = useState(false);
  const [dialogEditarEmpresa, setDialogEditarEmpresa] = useState(false);
  const [dialogConsultor, setDialogConsultor] = useState(false);
  const [dialogEditarConsultor, setDialogEditarConsultor] = useState(false);
  const [dialogInstalador, setDialogInstalador] = useState(false);
  const [dialogEditarInstalador, setDialogEditarInstalador] = useState(false);
  const [dialogFornecedor, setDialogFornecedor] = useState(false);
  const [dialogEditarFornecedor, setDialogEditarFornecedor] = useState(false);

  const [novaEmpresa, setNovaEmpresa] = useState({
    nome: "",
    cnpj: "",
    telefone: "",
    email: "",
    ativo: true
  });

  const [editarEmpresaData, setEditarEmpresaData] = useState<{
    id: number;
    nome: string;
    cnpj: string;
    telefone: string;
    email: string;
  } | null>(null);

  const [novoConsultor, setNovoConsultor] = useState({
    nome: "",
    celular: "",
    cpf_cnpj: "",
    email: "",
    ativo: true
  });

  const [novoInstalador, setNovoInstalador] = useState({
    nome: "",
    cpf_cnpj: "",
    contato: "",
    email: "",
    ativo: true
  });

  const [editarConsultorData, setEditarConsultorData] = useState<{
    id: string;
    nome: string;
    celular: string;
    cpf_cnpj: string;
    email: string;
    ativo: boolean;
  } | null>(null);

  const [editarInstaladorData, setEditarInstaladorData] = useState<{
    id: string;
    nome: string;
    cpf_cnpj: string;
    contato: string;
    email: string;
    ativo: boolean;
  } | null>(null);

  const [novoFornecedor, setNovoFornecedor] = useState({
    nome: "",
    cnpj: "",
    telefone: "",
    email: "",
    endereco: "",
    observacoes: "",
    ativo: true
  });

  const [editarFornecedorData, setEditarFornecedorData] = useState<{
    id: number;
    nome: string;
    cnpj: string;
    telefone: string;
    email: string;
    endereco: string;
    observacoes: string;
    ativo: boolean;
  } | null>(null);

  const handleSubmitEmpresa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!novaEmpresa.nome) return;
    
    try {
      await createEmpresa.mutateAsync(novaEmpresa);
      setNovaEmpresa({ nome: "", cnpj: "", telefone: "", email: "", ativo: true });
      setDialogEmpresa(false);
      toast.success("Empresa cadastrada com sucesso!");
    } catch (error) {
      toast.error("Erro ao cadastrar empresa");
    }
  };

  const handleEditarEmpresa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editarEmpresaData || !editarEmpresaData.nome) return;
    
    try {
      await editarEmpresa.mutateAsync(editarEmpresaData);
      setDialogEditarEmpresa(false);
      setEditarEmpresaData(null);
      toast.success("Empresa editada com sucesso!");
    } catch (error) {
      toast.error("Erro ao editar empresa");
    }
  };

  const handleExcluirEmpresa = async (id: number) => {
    try {
      await excluirEmpresa.mutateAsync(id);
      toast.success("Empresa excluída com sucesso!");
    } catch (error) {
      toast.error("Erro ao excluir empresa");
    }
  };

  const abrirDialogEditarEmpresa = (empresa: any) => {
    setEditarEmpresaData({
      id: empresa.id,
      nome: empresa.nome,
      cnpj: empresa.cnpj || "",
      telefone: empresa.telefone || "",
      email: empresa.email || ""
    });
    setDialogEditarEmpresa(true);
  };

  const handleSubmitConsultor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!novoConsultor.nome) return;
    
    try {
      await createConsultor.mutateAsync(novoConsultor);
      setNovoConsultor({ nome: "", celular: "", cpf_cnpj: "", email: "", ativo: true });
      setDialogConsultor(false);
      toast.success("Consultor cadastrado com sucesso!");
    } catch (error) {
      toast.error("Erro ao cadastrar consultor");
    }
  };

  const handleSubmitInstalador = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!novoInstalador.nome) return;
    
    try {
      await createInstalador.mutateAsync(novoInstalador);
      setNovoInstalador({ nome: "", cpf_cnpj: "", contato: "", email: "", ativo: true });
      setDialogInstalador(false);
      toast.success("Instalador cadastrado com sucesso!");
    } catch (error) {
      toast.error("Erro ao cadastrar instalador");
    }
  };

  const handleEditarConsultor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editarConsultorData || !editarConsultorData.nome) return;
    
    try {
      await editarConsultor.mutateAsync(editarConsultorData);
      setDialogEditarConsultor(false);
      setEditarConsultorData(null);
      toast.success("Consultor editado com sucesso!");
    } catch (error) {
      toast.error("Erro ao editar consultor");
    }
  };

  const handleExcluirConsultor = async (id: string) => {
    try {
      await excluirConsultor.mutateAsync(id);
      toast.success("Consultor excluído com sucesso!");
    } catch (error) {
      toast.error("Erro ao excluir consultor");
    }
  };

  const abrirDialogEditarConsultor = (consultor: any) => {
    setEditarConsultorData({
      id: consultor.id,
      nome: consultor.nome,
      celular: consultor.celular || "",
      cpf_cnpj: consultor.cpf_cnpj || "",
      email: consultor.email || "",
      ativo: consultor.ativo
    });
    setDialogEditarConsultor(true);
  };

  const handleEditarInstalador = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editarInstaladorData || !editarInstaladorData.nome) return;
    
    try {
      await editarInstalador.mutateAsync(editarInstaladorData);
      setDialogEditarInstalador(false);
      setEditarInstaladorData(null);
      toast.success("Instalador editado com sucesso!");
    } catch (error) {
      toast.error("Erro ao editar instalador");
    }
  };

  const handleExcluirInstalador = async (id: string) => {
    try {
      await excluirInstalador.mutateAsync(id);
      toast.success("Instalador excluído com sucesso!");
    } catch (error) {
      toast.error("Erro ao excluir instalador");
    }
  };

  const abrirDialogEditarInstalador = (instalador: any) => {
    setEditarInstaladorData({
      id: instalador.id,
      nome: instalador.nome,
      cpf_cnpj: instalador.cpf_cnpj || "",
      contato: instalador.contato || "",
      email: instalador.email || "",
      ativo: instalador.ativo
    });
    setDialogEditarInstalador(true);
  };

  const handleSubmitFornecedor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!novoFornecedor.nome.trim()) return;
    try {
      await createFornecedor.mutateAsync({
        nome: novoFornecedor.nome.trim(),
        cnpj: novoFornecedor.cnpj || undefined,
        telefone: novoFornecedor.telefone || undefined,
        email: novoFornecedor.email || undefined,
        endereco: novoFornecedor.endereco || undefined,
        observacoes: novoFornecedor.observacoes || undefined,
        ativo: novoFornecedor.ativo
      });
      setNovoFornecedor({ nome: "", cnpj: "", telefone: "", email: "", endereco: "", observacoes: "", ativo: true });
      setDialogFornecedor(false);
    } catch {
      // toast já no hook
    }
  };

  const handleEditarFornecedor = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editarFornecedorData || !editarFornecedorData.nome.trim()) return;
    try {
      await editarFornecedor.mutateAsync(editarFornecedorData);
      setDialogEditarFornecedor(false);
      setEditarFornecedorData(null);
    } catch {
      // toast já no hook
    }
  };

  const handleExcluirFornecedor = async (id: number) => {
    try {
      await excluirFornecedor.mutateAsync(id);
    } catch {
      // toast já no hook
    }
  };

  const abrirDialogEditarFornecedor = (fornecedor: any) => {
    setEditarFornecedorData({
      id: fornecedor.id,
      nome: fornecedor.nome || "",
      cnpj: fornecedor.cnpj || "",
      telefone: fornecedor.telefone || "",
      email: fornecedor.email || "",
      endereco: fornecedor.endereco || "",
      observacoes: fornecedor.observacoes || "",
      ativo: fornecedor.ativo ?? true
    });
    setDialogEditarFornecedor(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Cadastros</h2>
        <p className="text-gray-600">
          {isAdmin 
            ? "Gerencie empresas, consultores, equipamentos, planos, status e formas de pagamento"
            : "Gerencie consultores, instaladores e equipamentos"
          }
        </p>
      </div>

      <Tabs defaultValue={isAdmin ? "empresas" : "consultores"} className="w-full">
        <TabsList className={`grid w-full ${isAdmin ? 'grid-cols-8' : 'grid-cols-4'}`}>
          {isAdmin && <TabsTrigger value="empresas">Empresas</TabsTrigger>}
          <TabsTrigger value="consultores">Consultores</TabsTrigger>
          <TabsTrigger value="instaladores">Instaladores</TabsTrigger>
          <TabsTrigger value="fornecedores">Fornecedores</TabsTrigger>
          <TabsTrigger value="equipamentos">Equipamentos</TabsTrigger>
          {isAdmin && <TabsTrigger value="planos">Planos</TabsTrigger>}
          {isAdmin && <TabsTrigger value="status">Status</TabsTrigger>}
          {isAdmin && <TabsTrigger value="pagamento">Pagamento</TabsTrigger>}
        </TabsList>

        {isAdmin && (
          <TabsContent value="empresas" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Empresas Responsáveis</h3>
            {isAdmin && (
              <Dialog open={dialogEmpresa} onOpenChange={setDialogEmpresa}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90">
                    <Plus className="h-4 w-4 mr-2" />
                    Nova Empresa
                  </Button>
                </DialogTrigger>
                <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nova Empresa Responsável</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitEmpresa} className="space-y-4">
                  <div>
                    <Label htmlFor="nome_empresa">Nome da Empresa *</Label>
                    <Input
                      id="nome_empresa"
                      value={novaEmpresa.nome}
                      onChange={(e) => setNovaEmpresa({...novaEmpresa, nome: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cnpj_empresa">CNPJ</Label>
                    <Input
                      id="cnpj_empresa"
                      value={novaEmpresa.cnpj}
                      onChange={(e) => setNovaEmpresa({...novaEmpresa, cnpj: e.target.value})}
                      placeholder="00.000.000/0001-00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefone_empresa">Telefone</Label>
                    <Input
                      id="telefone_empresa"
                      value={novaEmpresa.telefone}
                      onChange={(e) => setNovaEmpresa({...novaEmpresa, telefone: e.target.value})}
                      placeholder="(11) 3456-7890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_empresa">E-mail</Label>
                    <Input
                      id="email_empresa"
                      type="email"
                      value={novaEmpresa.email}
                      onChange={(e) => setNovaEmpresa({...novaEmpresa, email: e.target.value})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogEmpresa(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90">
                      Cadastrar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {empresas.map((empresa) => (
              <Card key={empresa.id} className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Building className="h-4 w-4 text-green-600" />
                      {empresa.nome}
                    </div>
                    <div className="flex gap-1">
                      {isAdmin && (
                        <>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            title="Editar"
                            onClick={() => abrirDialogEditarEmpresa(empresa)}
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" title="Excluir">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir a empresa "{empresa.nome}"?
                              Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleExcluirEmpresa(empresa.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                        </>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {empresa.cnpj && <p><strong>CNPJ:</strong> {empresa.cnpj}</p>}
                  {empresa.telefone && <p><strong>Telefone:</strong> {empresa.telefone}</p>}
                  {empresa.email && <p><strong>E-mail:</strong> {empresa.email}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Modal de Edição de Empresa */}
          <Dialog open={dialogEditarEmpresa} onOpenChange={setDialogEditarEmpresa}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Empresa Responsável</DialogTitle>
              </DialogHeader>
              {editarEmpresaData && (
                <form onSubmit={handleEditarEmpresa} className="space-y-4">
                  <div>
                    <Label htmlFor="nome_empresa_edit">Nome da Empresa *</Label>
                    <Input
                      id="nome_empresa_edit"
                      value={editarEmpresaData.nome}
                      onChange={(e) => setEditarEmpresaData({...editarEmpresaData, nome: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cnpj_empresa_edit">CNPJ</Label>
                    <Input
                      id="cnpj_empresa_edit"
                      value={editarEmpresaData.cnpj}
                      onChange={(e) => setEditarEmpresaData({...editarEmpresaData, cnpj: e.target.value})}
                      placeholder="00.000.000/0001-00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefone_empresa_edit">Telefone</Label>
                    <Input
                      id="telefone_empresa_edit"
                      value={editarEmpresaData.telefone}
                      onChange={(e) => setEditarEmpresaData({...editarEmpresaData, telefone: e.target.value})}
                      placeholder="(11) 3456-7890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_empresa_edit">E-mail</Label>
                    <Input
                      id="email_empresa_edit"
                      type="email"
                      value={editarEmpresaData.email}
                      onChange={(e) => setEditarEmpresaData({...editarEmpresaData, email: e.target.value})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogEditarEmpresa(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      Salvar Alterações
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>
        )}

        <TabsContent value="consultores" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Consultores</h3>
            {(isAdmin || isOperator) && (
              <Dialog open={dialogConsultor} onOpenChange={setDialogConsultor}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Consultor
                  </Button>
                </DialogTrigger>
                <DialogContent>
                <DialogHeader>
                  <DialogTitle>Novo Consultor</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitConsultor} className="space-y-4">
                  <div>
                    <Label htmlFor="nome_consultor">Nome *</Label>
                    <Input
                      id="nome_consultor"
                      value={novoConsultor.nome}
                      onChange={(e) => setNovoConsultor({...novoConsultor, nome: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="celular_consultor">Celular</Label>
                    <Input
                      id="celular_consultor"
                      value={novoConsultor.celular}
                      onChange={(e) => setNovoConsultor({...novoConsultor, celular: e.target.value})}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cpf_cnpj_consultor">CPF/CNPJ</Label>
                    <Input
                      id="cpf_cnpj_consultor"
                      value={novoConsultor.cpf_cnpj}
                      onChange={(e) => setNovoConsultor({...novoConsultor, cpf_cnpj: e.target.value})}
                      placeholder="000.000.000-00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_consultor">E-mail</Label>
                    <Input
                      id="email_consultor"
                      type="email"
                      value={novoConsultor.email}
                      onChange={(e) => setNovoConsultor({...novoConsultor, email: e.target.value})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogConsultor(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90">
                      Cadastrar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {consultores.map((consultor) => (
              <Card key={consultor.id} className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-blue-600" />
                      {consultor.nome}
                    </div>
                    <div className="flex items-center gap-2">
                      {(isAdmin || isOperator) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => abrirDialogEditarConsultor(consultor)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir Consultor</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir o consultor "{consultor.nome}"? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleExcluirConsultor(consultor.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                        </>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {consultor.celular && <p><strong>Celular:</strong> {consultor.celular}</p>}
                  {consultor.cpf_cnpj && <p><strong>CPF/CNPJ:</strong> {consultor.cpf_cnpj}</p>}
                  {consultor.email && <p><strong>E-mail:</strong> {consultor.email}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Modal de Edição de Consultor */}
          <Dialog open={dialogEditarConsultor} onOpenChange={setDialogEditarConsultor}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Consultor</DialogTitle>
              </DialogHeader>
              {editarConsultorData && (
                <form onSubmit={handleEditarConsultor} className="space-y-4">
                  <div>
                    <Label htmlFor="nome_consultor_edit">Nome *</Label>
                    <Input
                      id="nome_consultor_edit"
                      value={editarConsultorData.nome}
                      onChange={(e) => setEditarConsultorData({...editarConsultorData, nome: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="celular_consultor_edit">Celular</Label>
                    <Input
                      id="celular_consultor_edit"
                      value={editarConsultorData.celular}
                      onChange={(e) => setEditarConsultorData({...editarConsultorData, celular: e.target.value})}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cpf_cnpj_consultor_edit">CPF/CNPJ</Label>
                    <Input
                      id="cpf_cnpj_consultor_edit"
                      value={editarConsultorData.cpf_cnpj}
                      onChange={(e) => setEditarConsultorData({...editarConsultorData, cpf_cnpj: e.target.value})}
                      placeholder="000.000.000-00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_consultor_edit">E-mail</Label>
                    <Input
                      id="email_consultor_edit"
                      type="email"
                      value={editarConsultorData.email}
                      onChange={(e) => setEditarConsultorData({...editarConsultorData, email: e.target.value})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogEditarConsultor(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      Salvar Alterações
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="instaladores" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Instaladores</h3>
            {(isAdmin || isOperator) && (
              <Dialog open={dialogInstalador} onOpenChange={setDialogInstalador}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Instalador
                  </Button>
                </DialogTrigger>
                <DialogContent>
                <DialogHeader>
                  <DialogTitle>Novo Instalador</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmitInstalador} className="space-y-4">
                  <div>
                    <Label htmlFor="nome_instalador">Nome *</Label>
                    <Input
                      id="nome_instalador"
                      value={novoInstalador.nome}
                      onChange={(e) => setNovoInstalador({...novoInstalador, nome: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cpf_cnpj_instalador">CPF/CNPJ</Label>
                    <Input
                      id="cpf_cnpj_instalador"
                      value={novoInstalador.cpf_cnpj}
                      onChange={(e) => setNovoInstalador({...novoInstalador, cpf_cnpj: e.target.value})}
                      placeholder="000.000.000-00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contato_instalador">Contato</Label>
                    <Input
                      id="contato_instalador"
                      value={novoInstalador.contato}
                      onChange={(e) => setNovoInstalador({...novoInstalador, contato: e.target.value})}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_instalador">E-mail</Label>
                    <Input
                      id="email_instalador"
                      type="email"
                      value={novoInstalador.email}
                      onChange={(e) => setNovoInstalador({...novoInstalador, email: e.target.value})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogInstalador(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-primary hover:bg-primary/90">
                      Cadastrar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {instaladores.map((instalador) => (
              <Card key={instalador.id} className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-orange-600" />
                      {instalador.nome}
                    </div>
                    <div className="flex items-center gap-2">
                      {(isAdmin || isOperator) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => abrirDialogEditarInstalador(instalador)}
                            className="h-7 w-7 p-0"
                          >
                            <Edit className="h-3 w-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Excluir Instalador</AlertDialogTitle>
                            <AlertDialogDescription>
                              Tem certeza que deseja excluir o instalador "{instalador.nome}"? Esta ação não pode ser desfeita.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancelar</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleExcluirInstalador(instalador.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Excluir
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                        </>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {instalador.cpf_cnpj && <p><strong>CPF/CNPJ:</strong> {instalador.cpf_cnpj}</p>}
                  {instalador.contato && <p><strong>Contato:</strong> {instalador.contato}</p>}
                  {instalador.email && <p><strong>E-mail:</strong> {instalador.email}</p>}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Modal de Edição de Instalador */}
          <Dialog open={dialogEditarInstalador} onOpenChange={setDialogEditarInstalador}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Instalador</DialogTitle>
              </DialogHeader>
              {editarInstaladorData && (
                <form onSubmit={handleEditarInstalador} className="space-y-4">
                  <div>
                    <Label htmlFor="nome_instalador_edit">Nome *</Label>
                    <Input
                      id="nome_instalador_edit"
                      value={editarInstaladorData.nome}
                      onChange={(e) => setEditarInstaladorData({...editarInstaladorData, nome: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cpf_cnpj_instalador_edit">CPF/CNPJ</Label>
                    <Input
                      id="cpf_cnpj_instalador_edit"
                      value={editarInstaladorData.cpf_cnpj}
                      onChange={(e) => setEditarInstaladorData({...editarInstaladorData, cpf_cnpj: e.target.value})}
                      placeholder="000.000.000-00"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contato_instalador_edit">Contato</Label>
                    <Input
                      id="contato_instalador_edit"
                      value={editarInstaladorData.contato}
                      onChange={(e) => setEditarInstaladorData({...editarInstaladorData, contato: e.target.value})}
                      placeholder="(11) 99999-9999"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_instalador_edit">E-mail</Label>
                    <Input
                      id="email_instalador_edit"
                      type="email"
                      value={editarInstaladorData.email}
                      onChange={(e) => setEditarInstaladorData({...editarInstaladorData, email: e.target.value})}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogEditarInstalador(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                      Salvar Alterações
                    </Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="fornecedores" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Fornecedores</h3>
            {(isAdmin || isOperator) && (
              <Dialog open={dialogFornecedor} onOpenChange={setDialogFornecedor}>
                <DialogTrigger asChild>
                  <Button className="bg-primary hover:bg-primary/90">
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Fornecedor
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Novo Fornecedor</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitFornecedor} className="space-y-4">
                    <div>
                      <Label htmlFor="nome_fornecedor">Nome / Razão Social *</Label>
                      <Input
                        id="nome_fornecedor"
                        value={novoFornecedor.nome}
                        onChange={(e) => setNovoFornecedor({ ...novoFornecedor, nome: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="cnpj_fornecedor">CNPJ</Label>
                      <Input
                        id="cnpj_fornecedor"
                        value={novoFornecedor.cnpj}
                        onChange={(e) => setNovoFornecedor({ ...novoFornecedor, cnpj: e.target.value })}
                        placeholder="00.000.000/0001-00"
                      />
                    </div>
                    <div>
                      <Label htmlFor="telefone_fornecedor">Telefone</Label>
                      <Input
                        id="telefone_fornecedor"
                        value={novoFornecedor.telefone}
                        onChange={(e) => setNovoFornecedor({ ...novoFornecedor, telefone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="email_fornecedor">E-mail</Label>
                      <Input
                        id="email_fornecedor"
                        type="email"
                        value={novoFornecedor.email}
                        onChange={(e) => setNovoFornecedor({ ...novoFornecedor, email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="endereco_fornecedor">Endereço</Label>
                      <Input
                        id="endereco_fornecedor"
                        value={novoFornecedor.endereco}
                        onChange={(e) => setNovoFornecedor({ ...novoFornecedor, endereco: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="observacoes_fornecedor">Observações</Label>
                      <Input
                        id="observacoes_fornecedor"
                        value={novoFornecedor.observacoes}
                        onChange={(e) => setNovoFornecedor({ ...novoFornecedor, observacoes: e.target.value })}
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button type="button" variant="outline" onClick={() => setDialogFornecedor(false)}>Cancelar</Button>
                      <Button type="submit" className="bg-primary hover:bg-primary/90">Cadastrar</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {fornecedores.map((fornecedor) => (
              <Card key={fornecedor.id} className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4 text-amber-600" />
                      {fornecedor.nome}
                      {!fornecedor.ativo && <span className="text-xs text-gray-500">(inativo)</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      {(isAdmin || isOperator) && fornecedor.ativo && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => abrirDialogEditarFornecedor(fornecedor)} className="h-7 w-7 p-0">
                            <Edit className="h-3 w-3" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50">
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir Fornecedor</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Tem certeza que deseja excluir o fornecedor &quot;{fornecedor.nome}&quot;? Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleExcluirFornecedor(fornecedor.id)} className="bg-red-600 hover:bg-red-700">
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {fornecedor.cnpj && <p><strong>CNPJ:</strong> {fornecedor.cnpj}</p>}
                  {fornecedor.telefone && <p><strong>Telefone:</strong> {fornecedor.telefone}</p>}
                  {fornecedor.email && <p><strong>E-mail:</strong> {fornecedor.email}</p>}
                  {fornecedor.endereco && <p><strong>Endereço:</strong> {fornecedor.endereco}</p>}
                  {fornecedor.observacoes && <p className="text-gray-600">{fornecedor.observacoes}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
          <Dialog open={dialogEditarFornecedor} onOpenChange={setDialogEditarFornecedor}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Fornecedor</DialogTitle>
              </DialogHeader>
              {editarFornecedorData && (
                <form onSubmit={handleEditarFornecedor} className="space-y-4">
                  <div>
                    <Label htmlFor="nome_fornecedor_edit">Nome / Razão Social *</Label>
                    <Input
                      id="nome_fornecedor_edit"
                      value={editarFornecedorData.nome}
                      onChange={(e) => setEditarFornecedorData({ ...editarFornecedorData, nome: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="cnpj_fornecedor_edit">CNPJ</Label>
                    <Input
                      id="cnpj_fornecedor_edit"
                      value={editarFornecedorData.cnpj}
                      onChange={(e) => setEditarFornecedorData({ ...editarFornecedorData, cnpj: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="telefone_fornecedor_edit">Telefone</Label>
                    <Input
                      id="telefone_fornecedor_edit"
                      value={editarFornecedorData.telefone}
                      onChange={(e) => setEditarFornecedorData({ ...editarFornecedorData, telefone: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="email_fornecedor_edit">E-mail</Label>
                    <Input
                      id="email_fornecedor_edit"
                      type="email"
                      value={editarFornecedorData.email}
                      onChange={(e) => setEditarFornecedorData({ ...editarFornecedorData, email: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="endereco_fornecedor_edit">Endereço</Label>
                    <Input
                      id="endereco_fornecedor_edit"
                      value={editarFornecedorData.endereco}
                      onChange={(e) => setEditarFornecedorData({ ...editarFornecedorData, endereco: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="observacoes_fornecedor_edit">Observações</Label>
                    <Input
                      id="observacoes_fornecedor_edit"
                      value={editarFornecedorData.observacoes}
                      onChange={(e) => setEditarFornecedorData({ ...editarFornecedorData, observacoes: e.target.value })}
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setDialogEditarFornecedor(false)}>Cancelar</Button>
                    <Button type="submit" className="bg-blue-600 hover:bg-blue-700">Salvar Alterações</Button>
                  </div>
                </form>
              )}
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="equipamentos">
          <Equipamentos />
        </TabsContent>

        {isAdmin && (
          <>
            <TabsContent value="planos">
              <Planos />
            </TabsContent>

            <TabsContent value="status">
              <StatusCustomizados />
            </TabsContent>

            <TabsContent value="pagamento">
              <FormasPagamento />
            </TabsContent>
          </>
        )}
      </Tabs>
    </div>
  );
}